import {
  Component,
  EventEmitter,
  Input,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import {
  CateringSummaryData,
  CateringSummaryEvent,
} from '../../../../services/dashboard/data/catering-summary.data';
import {
  cateringApprovalStatusColor,
  cateringApprovalStatusLabel,
  mergeCateringPreferenceSummary,
  preferenceComboSummaryLabel,
} from '../../../../constants/catering-summary-labels';
import { downloadCateringSummaryPdf } from '../../../../utils/catering-summary-pdf.util';

@Component({
  selector: 'app-duna-catering-summary-widget',
  standalone: true,
  imports: [MatButtonModule, MatIconModule],
  templateUrl: './duna-catering-summary-widget.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './duna-catering-summary-widget.component.scss',
  host: {
    class: 'duna-catering-summary-widget-host',
    '[class.fill-height]': 'fillHeight',
  },
})
export class DunaCateringSummaryWidgetComponent {
  @Input() data: CateringSummaryData | null = null;
  @Input() isLoading = false;
  @Input() fillHeight = false;
  @Input() showPdfDownload = false;
  @Input() pdfTitle = 'Forplejningsoversigt';
  @Input() allowEventNavigation = true;

  @Output() openPreferences = new EventEmitter<CateringSummaryEvent>();
  @Output() navigateToEvent = new EventEmitter<string>();

  preferenceLabel = preferenceComboSummaryLabel;
  approvalLabel = cateringApprovalStatusLabel;
  approvalColor = cateringApprovalStatusColor;

  mergedPreferences(
    preferences: Record<string, number> | null | undefined
  ): Record<string, number> {
    return mergeCateringPreferenceSummary(preferences);
  }

  preferenceCount(
    preferences: Record<string, number> | null | undefined,
    key: string
  ): number {
    return this.mergedPreferences(preferences)[key] ?? 0;
  }

  getPreferenceKeys(
    preferences: Record<string, number> | null | undefined
  ): string[] {
    const merged = this.mergedPreferences(preferences);
    if (!Object.keys(merged).length) {
      return [];
    }
    return Object.keys(merged)
      .filter((key) => (merged[key] ?? 0) > 0)
      .sort((a, b) =>
        preferenceComboSummaryLabel(a).localeCompare(
          preferenceComboSummaryLabel(b),
          'da'
        )
      );
  }

  formatDayDate(date: string): string {
    if (!date) {
      return '';
    }
    const parsed = new Date(date + 'T12:00:00');
    if (isNaN(parsed.getTime())) {
      return date;
    }
    return parsed.toLocaleDateString('da-DK', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
  }

  formatEventTime(event: CateringSummaryEvent): string {
    const start = event.start_time || '';
    const end = event.end_time || '';
    if (start && end && start !== end) {
      return `${start} – ${end}`;
    }
    return start || end || '—';
  }

  /** All catering-relevant participants incl. instructors (excludes waitlist, afbud, etc.). */
  participantCount(event: CateringSummaryEvent): number {
    return event.participant_count ?? event.current_pending_user_count ?? 0;
  }

  onOpenPreferences(event: CateringSummaryEvent): void {
    this.openPreferences.emit(event);
  }

  onNavigateToEvent(guid: string): void {
    if (guid) {
      this.navigateToEvent.emit(guid);
    }
  }

  onDownloadPdf(): void {
    downloadCateringSummaryPdf(this.data, this.pdfTitle);
  }
}
